import { formatAppointmentDateOnlyInClinic } from './clinicTime';

export const CONSENT_PREVIEW_STORAGE_KEY = 'podoclinic_consent_preview_v1';

function escapeHtml(s) {
  return String(s || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Campo rellenado o línea en blanco para firma */
function fill(value, blankLabel = '___________________________') {
  const v = String(value || '').trim();
  if (!v) {
    return `<span class="blank">${escapeHtml(blankLabel)}</span>`;
  }
  return `<span class="fill">${escapeHtml(v)}</span>`;
}

function parseConsentDateParts(signedAt) {
  const ymd = String(signedAt || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) {
    return { day: '', month: '', year: '', full: '' };
  }
  try {
    const day = formatAppointmentDateOnlyInClinic(ymd, 'd');
    const month = formatAppointmentDateOnlyInClinic(ymd, 'MMMM');
    const year = formatAppointmentDateOnlyInClinic(ymd, 'yyyy');
    const full = formatAppointmentDateOnlyInClinic(ymd, "d 'de' MMMM 'de' yyyy");
    return { day, month, year, full };
  } catch {
    return { day: '', month: '', year: '', full: ymd };
  }
}

/**
 * Cuerpo del consentimiento informado con datos del expediente (HTML).
 */
export function buildConsentContentHtml({
  patientName = '',
  podiatristName = '',
  signedAt = '',
  clinicPlace = '',
  folio = '',
}) {
  const { day, month, year } = parseConsentDateParts(signedAt);
  const folioLine = folio
    ? `<p class="folio"><strong>Folio:</strong> ${escapeHtml(folio)}</p>`
    : '';

  return `
  ${folioLine}
  <h2 class="doc-title">CONSENTIMIENTO INFORMADO PARA LA PRÁCTICA PODOLÓGICA</h2>

  <p class="justify">
    Antes de prestarle la atención podológica que usted requiere, por imperativo legal; es
    <strong>OBLIGATORIO</strong> que usted exprese su autorización por escrito para la realización del
    servicio podológico, se hará además una exploración física, serán procedimientos realizados por
    técnicas no invasivas y que podrían originar dolor, en caso de tener una complicación ajena a
    nuestro servicio. Así como prestación de tratamiento podológico paliativo, y en caso de ser
    necesaria otra intervención de mayor entidad se le informará y pedirá otro consentimiento para la
    realización de la misma y/o se le dará una referencia para el servicio médico que amerite su
    situación.
  </p>

  <p class="justify"><strong>Los riesgos derivados de la aplicación del tratamiento podológico son:</strong></p>
  <ol class="risks">
    <li>Dolor durante o tras la manipulación.</li>
    <li>Ligero sangrado durante la manipulación.</li>
    <li>Hematoma en la zona de punción.</li>
    <li>Irritación en la zona manipulada.</li>
    <li>Infección superficial en caso de no seguir las medidas de asepsia que le indicará el podólogo
      tratante (se resuelve con limpieza local y antisépticos-antibióticos tópicos).</li>
  </ol>

  <h3 class="section-title">DECLARACIÓN DE CONSENTIMIENTO DEL PACIENTE</h3>

  <p class="justify">
    Yo C. ${fill(patientName)} he leído la hoja de información que me ha entregado el C.
    ${fill(podiatristName)} podólogo.
    He comprendido las explicaciones que se me han facilitado, y el podólogo que me ha atendido ha
    permitido realizar todas las observaciones y me ha aclarado todas las dudas y preguntas que le he
    planteado. También comprendo que, en cualquier momento y sin necesidad de dar ninguna explicación,
    puedo revocar el consentimiento que ahora presto.
  </p>

  <p class="justify">
    Por ello manifiesto que me considero satisfecho/a con la información recibida y comprendo la
    indicación y los riesgos de este tratamiento/procedimiento. Y en tales condiciones de la misma forma
    <strong>AUTORIZO</strong> la práctica de técnica requerida, así mismo queda implícitamente autorizada
    la exploración física previa, así como la toma de fotografías clínicas autorizadas con fines
    clínicos, de seguimiento terapéutico y documentación del expediente.
  </p>

  <p class="justify date-line">
    En ${fill(clinicPlace)}, a ${fill(day, '____')} de ${fill(month, '________________________')} de
    ${fill(year, '________')}.
  </p>

  <p class="authorize-label"><strong>AUTORIZO:</strong></p>
  <div class="signature-patient">${fill(patientName, '&nbsp;')}</div>
  <p class="sig-hint">Nombre y firma del paciente o representante legal</p>
  `;
}

/** Estilos para la vista previa React en /admin/consentimiento-preview */
export const CONSENT_PRINT_PAGE_STYLES = `
  body.consent-preview-open {
    margin: 0;
    background: #fff;
  }
  body.consent-preview-open #root {
    min-height: 100vh;
  }
  .consent-print-page {
    box-sizing: border-box;
    font-family: Georgia, "Times New Roman", serif;
    font-size: 12pt;
    line-height: 1.6;
    color: #111;
    max-width: 210mm;
    margin: 0 auto;
    padding: 18mm 16mm;
    background: #fff;
  }
  .consent-print-page * { box-sizing: border-box; }
  .consent-print-page .clinic {
    text-align: center;
    font-family: system-ui, sans-serif;
    font-size: 11pt;
    color: #444;
    margin-bottom: 6mm;
  }
  .consent-print-page .doc-content .doc-title {
    font-family: system-ui, sans-serif;
    font-size: 13pt;
    text-align: center;
    margin: 0 0 6mm;
    text-transform: uppercase;
    letter-spacing: 0.02em;
  }
  .consent-print-page .doc-content .section-title {
    font-size: 11pt;
    margin: 6mm 0 3mm;
    text-transform: uppercase;
  }
  .consent-print-page .doc-content .justify { text-align: justify; margin: 0 0 4mm; }
  .consent-print-page .doc-content .risks { margin: 0 0 5mm 6mm; padding-left: 4mm; }
  .consent-print-page .doc-content .risks li { margin-bottom: 2mm; }
  .consent-print-page .doc-content .fill {
    font-weight: 600;
    text-decoration: underline;
    text-underline-offset: 2px;
  }
  .consent-print-page .doc-content .blank {
    color: #555;
    letter-spacing: 0.05em;
  }
  .consent-print-page .doc-content .folio { font-size: 10pt; color: #444; margin-bottom: 4mm; }
  .consent-print-page .doc-content .date-line { margin-top: 6mm; }
  .consent-print-page .doc-content .authorize-label { margin-top: 8mm; margin-bottom: 2mm; }
  .consent-print-page .doc-content .signature-patient {
    border-bottom: 1px solid #222;
    min-height: 14mm;
    margin: 4mm 0 2mm;
    max-width: 80mm;
  }
  .consent-print-page .doc-content .sig-hint { font-size: 10pt; color: #444; margin: 0; }
  .consent-print-page .consent-print-toolbar {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    background: #1a4d3a;
    color: #fff;
    padding: 10px 16px;
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    align-items: center;
    z-index: 100;
    font-family: system-ui, sans-serif;
    font-size: 14px;
  }
  .consent-print-page .consent-print-toolbar button {
    background: #fff;
    color: #1a4d3a;
    border: none;
    padding: 8px 16px;
    border-radius: 999px;
    font-weight: 600;
    cursor: pointer;
  }
  .consent-print-page .consent-print-toolbar button:hover { background: #e8f5ee; }
  .consent-print-page .toolbar-hint { opacity: 0.9; font-size: 12px; flex: 1; min-width: 200px; }
  .consent-print-page.has-toolbar { padding-top: calc(18mm + 56px); }
  @media print {
    body.consent-preview-open { background: #fff; }
    .consent-print-page .consent-print-toolbar { display: none !important; }
    .consent-print-page.has-toolbar { padding-top: 18mm; }
  }
`;

export function consentPreviewUrl({ autoPrint = false } = {}) {
  const qs = autoPrint ? '?print=1' : '';
  return `${window.location.origin}/admin/consentimiento-preview${qs}`;
}

/**
 * HTML listo para imprimir / guardar como PDF desde el navegador.
 */
export function buildConsentDocumentHtml({
  clinicName = 'PodoclinicAM',
  patientName = '',
  signedAt = '',
  podiatristName = '',
  podiatristLicense = '',
  clinicPlace = '',
  folio = '',
}) {
  const body = buildConsentContentHtml({
    patientName,
    podiatristName,
    signedAt,
    clinicPlace,
    folio,
  });

  return `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>Consentimiento informado — ${escapeHtml(patientName || 'Paciente')}</title>
  <style>
    * { box-sizing: border-box; }
    body {
      font-family: Georgia, "Times New Roman", serif;
      font-size: 12pt;
      line-height: 1.6;
      color: #111;
      max-width: 210mm;
      margin: 0 auto;
      padding: 18mm 16mm;
      background: #fff;
    }
    .clinic {
      text-align: center;
      font-family: system-ui, sans-serif;
      font-size: 11pt;
      color: #444;
      margin-bottom: 6mm;
    }
    .doc-content .doc-title {
      font-family: system-ui, sans-serif;
      font-size: 13pt;
      text-align: center;
      margin: 0 0 6mm;
      text-transform: uppercase;
      letter-spacing: 0.02em;
    }
    .doc-content .section-title {
      font-size: 11pt;
      margin: 6mm 0 3mm;
      text-transform: uppercase;
    }
    .doc-content .justify { text-align: justify; margin: 0 0 4mm; }
    .doc-content .risks { margin: 0 0 5mm 6mm; padding-left: 4mm; }
    .doc-content .risks li { margin-bottom: 2mm; }
    .doc-content .fill {
      font-weight: 600;
      text-decoration: underline;
      text-underline-offset: 2px;
    }
    .doc-content .blank {
      color: #555;
      letter-spacing: 0.05em;
    }
    .doc-content .folio { font-size: 10pt; color: #444; margin-bottom: 4mm; }
    .doc-content .date-line { margin-top: 6mm; }
    .doc-content .authorize-label { margin-top: 8mm; margin-bottom: 2mm; }
    .doc-content .signature-patient {
      border-bottom: 1px solid #222;
      min-height: 14mm;
      margin: 4mm 0 2mm;
      max-width: 80mm;
    }
    .doc-content .sig-hint { font-size: 10pt; color: #444; margin: 0; }
    .toolbar {
      position: fixed;
      top: 0; left: 0; right: 0;
      background: #1a4d3a;
      color: #fff;
      padding: 10px 16px;
      display: flex;
      flex-wrap: wrap;
      gap: 10px;
      align-items: center;
      z-index: 100;
      font-family: system-ui, sans-serif;
      font-size: 14px;
    }
    .toolbar button {
      background: #fff;
      color: #1a4d3a;
      border: none;
      padding: 8px 16px;
      border-radius: 999px;
      font-weight: 600;
      cursor: pointer;
    }
    .toolbar button:hover { background: #e8f5ee; }
    .toolbar-hint { opacity: 0.9; font-size: 12px; flex: 1; min-width: 200px; }
    body.has-toolbar { padding-top: 56px; }
    @media print {
      .toolbar { display: none !important; }
      body.has-toolbar { padding-top: 18mm; }
    }
  </style>
</head>
<body class="has-toolbar">
  <div class="toolbar">
    <button type="button" onclick="window.print()">Imprimir / Guardar PDF</button>
    <button type="button" onclick="window.close()">Cerrar</button>
    <span class="toolbar-hint">En el diálogo de impresión elija «Guardar como PDF» o imprima en papel.</span>
  </div>
  <p class="clinic">${escapeHtml(clinicName)}</p>
  <div class="doc-content">${body}</div>
</body>
</html>`;
}

export function openConsentPreview(payload, { autoPrint = false } = {}) {
  const serialized = JSON.stringify(payload);
  try {
    localStorage.setItem(CONSENT_PREVIEW_STORAGE_KEY, serialized);
    sessionStorage.setItem(CONSENT_PREVIEW_STORAGE_KEY, serialized);
  } catch {
    /* ignore */
  }
  const win = window.open(consentPreviewUrl({ autoPrint }), '_blank', 'noopener,noreferrer');
  if (!win) {
    return { ok: false, reason: 'popup_blocked' };
  }
  return { ok: true };
}

export function readConsentPreviewPayload() {
  try {
    const raw =
      localStorage.getItem(CONSENT_PREVIEW_STORAGE_KEY) ||
      sessionStorage.getItem(CONSENT_PREVIEW_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}
